import { useAuth, useClerk } from '@clerk/clerk-react'
import { ReactNode, useEffect, useState } from 'react'
import { Navigate } from 'react-router-dom'
import { clearSession } from '@/utils/clearSession'

interface ProtectedRouteProps {
  children: ReactNode
}

export default function ProtectedRoute({ children }: ProtectedRouteProps) {
  const { isSignedIn, isLoaded } = useAuth()
  const clerk = useClerk()
  const [hasCheckedSession, setHasCheckedSession] = useState(false)

  // Check session validity on mount
  // Note: This is a lightweight check - Clerk's useAuth hook already handles most validation
  // We only verify that we can actually get a token (which validates the session is active)
  useEffect(() => {
    if (!isLoaded) {
      return
    }

    if (isSignedIn) {
      // Verify session is actually valid by checking if we can get a token
      // This catches edge cases where Clerk thinks we're signed in but the session is invalid
      const checkSession = async () => {
        try {
          const session = clerk.session
          if (!session) {
            // No session object - sign out
            console.warn('[ProtectedRoute] No session object available, signing out...')
            await clerk.signOut()
            setHasCheckedSession(true)
            return
          }

          // Try to get a token - this validates the session is actually active
          const token = await session.getToken()
          if (!token) {
            // Session exists but token is invalid - sign out
            console.warn('[ProtectedRoute] Session exists but token is invalid, signing out...')
            await clerk.signOut()
          }
        } catch (error) {
          // Token fetch failed - session is invalid
          console.warn('[ProtectedRoute] Failed to get session token, signing out...', error)
          try {
            await clerk.signOut()
          } catch {
            // If signOut fails, use clearSession utility to force cleanup
            clearSession()
            return // clearSession redirects, so we don't need to continue
          }
        } finally {
          setHasCheckedSession(true)
        }
      }

      checkSession()
    } else {
      // Not signed in - no need to check session
      setHasCheckedSession(true)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoaded, isSignedIn]) // Only depend on isLoaded and isSignedIn, not clerk object

  // Show loading state while checking auth
  if (!isLoaded || (isSignedIn && !hasCheckedSession)) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <p className="text-text-secondary">Loading...</p>
        </div>
      </div>
    )
  }

  // Redirect to login if not signed in
  if (!isSignedIn) {
    return <Navigate to="/admin/login" replace />
  }

  // Render protected content if signed in
  return <>{children}</>
}

